import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/app/AppShell";
import ReadinessGauge from "../components/app/ReadinessGauge";
import { useT } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Brain, Target, ArrowUpRight, Bell, Sparkle, ClockClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import StreakWidget from "../components/app/StreakWidget";
import DomainRadar from "../components/app/DomainRadar";
import ActivityHeatmap from "../components/app/ActivityHeatmap";
import DifficultyDonut from "../components/app/DifficultyDonut";
import AccuracyByDifficulty from "../components/app/AccuracyByDifficulty";
import ExamCountdown from "../components/app/ExamCountdown";
import SEOHead from "../components/app/SEOHead";
import DomainSparklines from "../components/app/DomainSparklines";
import OnboardingTour from "../components/app/OnboardingTour";

export default function Dashboard() {
  const { t, tDomain, lang } = useT();
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachMsg, setCoachMsg] = useState("");
  const [coachLoading, setCoachLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [focus, setFocus] = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);

  const loadFocus = async (refresh = false) => {
    setFocusLoading(true);
    try {
      const { data } = await api.get(`/ai/daily-focus?lang=${lang}${refresh ? "&refresh=true" : ""}`);
      setFocus(data.plan);
    } catch (e) {
      if (refresh && e?.response?.status === 429) {
        toast.info(t("dash.focus_cooldown"));
      }
    } finally {
      setFocusLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, tk, n, an] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/tasks"),
        api.get("/notifications"),
        api.get("/dashboard/analytics").catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setTasks(tk.data);
      setNotifications(n.data);
      setAnalytics(an.data);
      loadFocus(false);
    } catch (e) {
      toast.error(t("dash.load_failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  // Re-fetch daily focus when the language changes so plan text stays consistent
  // with the UI language.
  useEffect(() => {
    if (!loading) loadFocus(false);
  }, [lang]); // eslint-disable-line

  const askCoach = async () => {
    setCoachLoading(true);
    try {
      const { data } = await api.post("/ai/coach", { message: t("dash.coach_title") });
      setCoachMsg(data.message);
    } catch (e) {
      toast.error(t("dash.coach_unavailable"));
    } finally {
      setCoachLoading(false);
    }
  };

  const toggleTask = async (tk) => {
    try {
      await api.patch(`/tasks/${tk.task_id}`, { completed: !tk.completed });
      load();
    } catch {
      toast.error(t("common.failed"));
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="h-64 grid place-items-center" data-testid="dashboard-loading">
          <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" />
        </div>
      </AppShell>
    );
  }

  const open = tasks.filter((tk) => !tk.completed).slice(0, 6);
  const domainEntries = Object.entries(summary?.domain_scores || {});
  const firstName = summary?.user?.name?.split(" ")[0] || t("dash.greeting_default");

  return (
    <AppShell>
      <SEOHead pageKey="dashboard" />
      <OnboardingTour />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#002FA7]">{t("dash.overline")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="dashboard-title">
            {t("dash.greeting", { name: firstName })}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <PdfDownloadButton />
          <Link to="/practice" className="btn-secondary" data-testid="dashboard-practice-button">{t("nav.practice")}</Link>
          <Link to="/mock" className="btn-primary" data-testid="dashboard-mock-button">{t("dash.start_mock")}</Link>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <StreakWidget streak={summary?.streak} />

        <div className="lg:col-span-3" data-testid="exam-countdown-wrap">
          <ExamCountdown />
        </div>

        {summary?.review?.due > 0 && (
          <Link to="/review" className="card lg:col-span-3 card-hover relative !bg-[#FFE4E6] !border-[#E11D48]/30" data-testid="review-cta-card">
            <div className="overline flex items-center gap-1 text-[#E11D48]"><ClockClockwise size={12} weight="fill" /> {t("review.dashboard_label")}</div>
            <div className="font-display text-5xl font-bold mt-2 text-[#E11D48]">{summary.review.due}</div>
            <div className="text-sm text-slate-700 mt-2">{t("review.dashboard_due", { n: summary.review.due })}</div>
            <div className="text-xs text-slate-500 mt-3">{t("review.start")} →</div>
          </Link>
        )}

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card lg:col-span-4">
          <div className="overline">{t("dash.readiness")}</div>
          <div className="mt-4 grid place-items-center">
            <ReadinessGauge value={summary?.readiness_score || 0} label={summary?.readiness_label || ""} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-center">
            <Stat label={t("dash.last_score")} value={`${summary?.last_score || 0}%`} />
            <Stat label={t("dash.attempts")} value={summary?.attempts_count || 0} />
            <Stat label={t("dash.solved")} value={summary?.questions_solved || 0} />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card lg:col-span-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline">{t("dash.domain_breakdown")}</div>
              <h3 className="font-display text-xl font-semibold mt-1">{t("dash.domain_subtitle")}</h3>
            </div>
            <Target size={22} className="text-slate-400" />
          </div>
          {domainEntries.length === 0 ? (
            <div className="mt-6 text-slate-500 text-sm">
              {t("dash.no_attempts")} <Link to="/practice" className="text-[#002FA7] font-semibold">{t("dash.no_attempts_link")}</Link> {t("dash.no_attempts_suffix")}
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {domainEntries.map(([d, v]) => (
                <div key={d}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{tDomain(d)}</span>
                    <span className="font-mono">{v}%</span>
                  </div>
                  <div className="mt-1 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${v < 60 ? "bg-[#E11D48]" : v < 75 ? "bg-[#D97706]" : "bg-[#002FA7]"}`} style={{ width: `${v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {summary?.weak_domains?.length > 0 && (
            <div className="mt-6 text-xs text-slate-500">
              {t("dash.weakest")}: <span className="font-semibold text-[#E11D48]">{summary.weak_domains.map(tDomain).join(", ")}</span>
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card lg:col-span-3" data-testid="ai-focus-card">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="overline text-[#002FA7] flex items-center gap-1"><Sparkle size={12} weight="fill" /> {t("dash.coach")}</div>
              <h3 className="font-display text-lg font-semibold mt-1 leading-snug">
                {focusLoading && !focus ? t("dash.focus_generating") : (focus?.headline || t("dash.coach_title"))}
              </h3>
            </div>
            <button
              onClick={() => loadFocus(true)}
              disabled={focusLoading}
              title={t("dash.focus_refresh")}
              aria-label={t("dash.focus_refresh")}
              className="p-2 rounded-md bg-slate-100 hover:bg-[#DBEAFE] text-slate-700 hover:text-[#002FA7] transition-colors disabled:opacity-50"
              data-testid="ai-focus-refresh-button"
            >
              <ClockClockwise size={16} weight="bold" className={focusLoading ? "animate-spin" : ""} />
            </button>
          </div>

          {focus?.items?.length > 0 && (
            <ol className="mt-4 space-y-3">
              {focus.items.map((it, i) => (
                <li key={i} className="flex gap-3" data-testid={`ai-focus-item-${i}`}>
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#DBEAFE] text-[#002FA7] text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-baseline gap-2">
                      <span className="truncate">{it.title}</span>
                      <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">{it.minutes} {t("dash.focus_minutes")}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5 leading-snug">{it.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {!focus && !focusLoading && (
            <p className="text-sm text-slate-500 mt-2">{t("dash.coach_sub")}</p>
          )}

          <button
            onClick={askCoach}
            disabled={coachLoading}
            className="btn-secondary mt-4 w-full justify-center text-xs"
            data-testid="coach-ask-button"
          >
            <Brain size={14} weight="bold" /> {coachLoading ? t("dash.coach_thinking") : t("dash.focus_chat")}
          </button>
          {coachMsg && (
            <div className="ai-callout mt-3 text-xs whitespace-pre-line" data-testid="coach-message">{coachMsg}</div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="card lg:col-span-8">
          <div className="overline">{t("dash.progress_overline")}</div>
          <h3 className="font-display text-xl font-semibold mt-1">{t("dash.progress_title")}</h3>
          <div className="h-64 mt-4">
            {summary?.history?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.history}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, color: "#fff" }} />
                  <Line type="monotone" dataKey="score" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="readiness" stroke="#E11D48" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-slate-400">{t("dash.no_history")}</div>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card lg:col-span-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">{t("nav.tasks")}</div>
              <h3 className="font-display text-xl font-semibold mt-1">{t("dash.tasks_open", { n: summary?.open_tasks || 0 })}</h3>
            </div>
            <Link to="/tasks" className="text-sm font-semibold text-[#002FA7] hover:underline" data-testid="dashboard-view-all-tasks">{t("dash.view_all")} <ArrowUpRight size={14} className="inline" /></Link>
          </div>
          <ul className="mt-4 space-y-2" data-testid="dashboard-task-list">
            {open.length === 0 && <li className="text-sm text-slate-500">{t("dash.no_tasks")}</li>}
            {open.map((tk) => (
              <li key={tk.task_id} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                <input type="checkbox" className="!w-4 !h-4 mt-1" checked={tk.completed} onChange={() => toggleTask(tk)} data-testid={`task-toggle-${tk.task_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{tk.title_key ? t(tk.title_key, { domain: tk.domain ? tDomain(tk.domain) : "" }) : tk.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${tk.priority === "high" ? "bg-[#FFE4E6] text-[#E11D48]" : tk.priority === "low" ? "bg-slate-100 text-slate-500" : "bg-[#FEF3C7] text-[#D97706]"}`}>{t(`common.${tk.priority}`)}</span>
                    {tk.domain && <span>· {tDomain(tk.domain)}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card lg:col-span-12">
          <div className="overline flex items-center gap-2"><Bell size={12} /> {t("dash.activity")}</div>
          <h3 className="font-display text-xl font-semibold mt-1">{t("dash.recent_updates")}</h3>
          {summary?.inactivity_days > 2 && (
            <div className="mt-3 ai-callout border-l-[#D97706] bg-[#FEF3C7] text-sm">
              <strong>{t("dash.inactivity", { days: summary.inactivity_days })}</strong> {t("dash.inactivity_suffix")}
            </div>
          )}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notifications-list">
            {notifications.length === 0 && <div className="text-sm text-slate-500">{t("dash.no_notifications")}</div>}
            {notifications.slice(0, 6).map((n) => (
              <div key={n.notification_id} className="card !p-4 card-hover">
                <div className="overline text-[#002FA7]">{n.type.toUpperCase()}</div>
                <div className="font-semibold text-sm mt-1">{n.title}</div>
                <div className="text-xs text-slate-500 mt-1">{n.body}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ============ ANALYTICS SECTION ============ */}
      {analytics && (
        <>
          <div className="mt-12 flex items-end justify-between flex-wrap gap-3" data-testid="analytics-section-header">
            <div>
              <div className="overline text-[#002FA7]">{t("analytics.section").toUpperCase()}</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
                {t("analytics.section")}
              </h2>
            </div>
            {analytics?.totals && (
              <div className="flex gap-6 text-right">
                <div>
                  <div className="overline">{t("analytics.totals_overline")}</div>
                  <div className="font-display text-xl font-bold mt-1 font-mono">{analytics.totals.questions_answered}</div>
                  <div className="text-xs text-slate-500">{t("common.questions").toLowerCase()}</div>
                </div>
                <div>
                  <div className="overline">{t("analytics.overall_accuracy")}</div>
                  <div className="font-display text-xl font-bold mt-1 font-mono">{analytics.totals.overall_accuracy}%</div>
                </div>
                <div>
                  <div className="overline">{t("analytics.total_minutes")}</div>
                  <div className="font-display text-xl font-bold mt-1 font-mono">{Math.round(analytics.totals.total_study_minutes)}</div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="contents">
              <DomainRadar data={analytics.radar} />
              <ActivityHeatmap data={analytics.heatmap} />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="contents">
              <DifficultyDonut data={analytics.difficulty_split} />
              <AccuracyByDifficulty data={analytics.accuracy_by_difficulty} />
              <DomainSparklines data={analytics.domain_sparklines} />
            </motion.div>
          </div>
        </>
      )}
    </AppShell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}


function PdfDownloadButton() {
  const { t, lang } = useT();
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("pmp_token");
      const res = await fetch(`${apiBase}/api/reports/readiness.pdf?lang=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PMP-Readiness-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PDF download failed:", e);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <button onClick={downloadPdf} disabled={downloading} className="btn-secondary !py-2 text-sm" data-testid="dashboard-pdf-download">
      {downloading ? t("dash.pdf_downloading") : t("dash.pdf_download")}
    </button>
  );
}
